import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { IconRightCaret, IconInfo } from '@belong/icons';
import { Money, Text, Tooltip } from '@belong/ui';
import {
  BalanceInfoModel,
  GroupedBalancesInfoModel,
  BillStatusInfo,
  BillingInfoModel,
} from 'api/models.v2';
import cx from 'classnames';
import {
  getBalanceSubtitle,
  getBalanceSubtitleStyles,
} from 'pages/Accounts/utils/get-balance-subtitle';
import { EARNINGS_STRINGS } from '../../Pages/YourPortfolio/Earnings/earnings.strings';
import { BalanceItemIcon } from '../balance-item-icon/balance-item-icon';

type BalanceItemProps = {
  address: string;
  billStatusInfo?: BillStatusInfo;
  billingInfo?: BillingInfoModel;
  isResident?: boolean;
  // `sourceKey` is optional on `GroupedBalancesInfoModel`, and it is what gets
  // passed here; the implementation behind this prop takes `any`.
  navigateToFinanceDetails: (
    id: string | number | undefined,
    balance: GroupedBalancesInfoModel,
    home: string,
  ) => void;
  handleShowBalanceDetailsModal?: (
    balance: GroupedBalancesInfoModel,
    home: string,
    billingInfo?: BillingInfoModel,
    standalone?: boolean,
    isAutoSplittable?: boolean,
  ) => void;
  noBorder?: boolean;
  hideSplitIt?: boolean;
  isFastMaintenanceEnabled: boolean;
  isAutoSplittable?: boolean;
  balanceItem: GroupedBalancesInfoModel;
};

export function BalanceItem({
  navigateToFinanceDetails,
  handleShowBalanceDetailsModal,
  address,
  isAutoSplittable,
  billingInfo,
  billStatusInfo,
  isFastMaintenanceEnabled,
  hideSplitIt = false,
  isResident = false,
  noBorder = false,
  balanceItem,
}: BalanceItemProps) {
  const {
    sourceType,
    sourceKey,
    description,
    netBalance,
    isFinanceable,
    balances,
    isDelayed,
  } = balanceItem;
  const { width } = useWindowSize();

  const isMobile = width < BREAKPOINTS_WIDTHS.SM;

  function handleFinanceItem(
    id: string | undefined,
    balance: GroupedBalancesInfoModel,
  ) {
    if (sourceType === 'Finance') {
      navigateToFinanceDetails(id, balance, address);
    }

    if (sourceType === 'GroupKey' || sourceType === 'Balance') {
      if (handleShowBalanceDetailsModal) {
        handleShowBalanceDetailsModal(
          balance,
          address,
          billingInfo,
          false,
          isAutoSplittable,
        );
      }
    }
  }

  return (
    <button
      className={cx(
        'w-full flex items-center justify-between mb-2xs p-2xs pl-0 lg:pl-xs hover:bg-[#F3FDFA] rounded-[10px] cursor-pointer',
        noBorder && 'pl-0 ml-0',
      )}
      onClick={() =>
        handleFinanceItem(sourceKey, {
          ...balanceItem,
          balances:
            sourceType === 'Balance'
              ? // A `Balance` group item stands in for its own single balance;
                // the two models overlap but are declared separately.
                ([balanceItem] as unknown as BalanceInfoModel[])
              : balances,
        })
      }
      type="button"
    >
      <div>
        <div className="text-left">
          <Text as="span" className="text-p1 md:text-body">
            {description}
          </Text>
          {!isResident && !hideSplitIt && (
            <BalanceItemIcon
              isFinanceable={isFinanceable}
              className="ml-xs inline-flex p-2xs align-middle"
            />
          )}
          {isDelayed && (
            <Text
              as="span"
              variant="p1"
              className="ml-xs inline-block rounded-small bg-red px-xs align-middle text-white"
            >{`${EARNINGS_STRINGS['delayed.label']}`}</Text>
          )}
        </div>
        {isFastMaintenanceEnabled && (
          <div className="flex gap-xs">
            <Text
              variant="p1"
              className={cx(
                'text-left',
                getBalanceSubtitleStyles(billStatusInfo, isDelayed),
              )}
            >
              {getBalanceSubtitle(balanceItem)}
            </Text>
            {balanceItem.isDueDateAnEstimation && (
              <Tooltip
                arrowProps={{ className: 'fill-dark-navy' }}
                contentProps={{ side: isMobile ? 'top' : 'right' }}
                content={
                  <div className="max-w-md rounded bg-dark-navy p-2sm">
                    <Text
                      className="text-left text-white"
                      variant="p1"
                      fontWeight="semibold"
                    >
                      {balanceItem.isDelayed
                        ? EARNINGS_STRINGS['due_date_delayed.tooltip']
                        : EARNINGS_STRINGS['due_date_estimation.tooltip']}
                    </Text>
                  </div>
                }
              >
                <button
                  className="cursor-pointer fill-navy"
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconInfo width={20} />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="ml-2xs flex items-center justify-end md:ml-0">
        <Money
          className="text-p1 font-semibold md:text-body"
          value={netBalance}
          format="DOLLARS_WITH_CENTS"
        />
        <IconRightCaret height={15} className="ml-xs" />
      </div>
    </button>
  );
}
